/** QuickPlan — Modèle de données (zod), partagé client / serveur.
 *
 * Unités : millimètres, degrés. Repère : X vers la droite, Y vers le fond, Z vers le haut.
 *
 *  Composant (modèle de bibliothèque, paramétrique)
 *    params  : [{ key, label, default, min, max, step, unit, options }]
 *    parts   : primitives (box | cyl | extrude) dont les cotes sont des EXPRESSIONS
 *              sur les paramètres ("L", "H-2*E"…), éventuellement répétées (repeat)
 *    ports   : points de connexion nommés (accroche, pose par port)
 *    bom     : comment le composant compte dans la nomenclature (pièce, débit, rien)
 *    kit     : quincaillerie livrée avec (vis, écrous…) — quantités en expressions
 *
 *  Assemblage type (template) : groupe d'instances paramétré (caisson, cadre, baie…)
 *
 *  Projet
 *    components : composants propres au projet (créés à la main ou par l'IA)
 *    groups     : sous-ensembles (arborescence)
 *    instances  : composants posés { componentId, params, pos, rot, groupId, step }
 *    extras     : lignes de nomenclature libres
 */
import { z } from 'zod';

export function newId(prefix = 'x') {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

const Num = z.number().finite();
/** Une cote : nombre ou expression sur les paramètres. */
export const Expr = z.union([Num, z.string().min(1).max(200)]);
const Vec3 = z.tuple([Expr, Expr, Expr]);
const NumVec3 = z.tuple([Num, Num, Num]);
const Color = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const Id = z.string().regex(/^[\w-]+$/).max(80);

export const ParamSchema = z.object({
  key: z.string().regex(/^[A-Za-z_]\w*$/),
  label: z.string().max(80).optional(),
  default: Num,
  min: Num.optional(),
  max: Num.optional(),
  step: Num.optional(),
  unit: z.string().max(10).optional(),
  options: z.array(Num).max(50).optional(),
});

const Repeat = z.object({ count: Expr, var: z.string().regex(/^[A-Za-z_]\w*$/).default('i') }).optional();

const PartBase = {
  name: z.string().max(80).optional(),
  color: Color.optional(),
  /** Opacité (vitrage, plexiglas) : 0,05 à 1. */
  opacity: Num.min(0.05).max(1).optional(),
  /** Rotation propre de la primitive (degrés, ordre XYZ) autour de son point `pos`. */
  rot: Vec3.optional(),
  repeat: Repeat,
};

export const PartSchema = z.discriminatedUnion('type', [
  /** Pavé : `pos` = coin minimal, `size` = [x, y, z]. */
  z.object({ type: z.literal('box'), size: Vec3, pos: Vec3.default([0, 0, 0]), ...PartBase }),
  /** Cylindre : `pos` = centre de la base, `r` rayon, `h` longueur le long de `axis`. */
  z.object({
    type: z.literal('cyl'), r: Expr, h: Expr, pos: Vec3.default([0, 0, 0]),
    axis: z.enum(['x', 'y', 'z']).default('z'), ...PartBase,
  }),
  /** Extrusion d'une section le long de `axis`, sur `length`, depuis `pos` (section centrée). */
  z.object({
    type: z.literal('extrude'),
    section: z.union([
      z.string().max(60),
      z.object({ outer: z.array(z.tuple([Num, Num])).min(3).max(2000), holes: z.array(z.array(z.tuple([Num, Num])).min(3)).max(100).default([]) }),
    ]),
    length: Expr, pos: Vec3.default([0, 0, 0]), axis: z.enum(['x', 'y', 'z']).default('x'), ...PartBase,
  }),
]);

export const PortSchema = z.object({
  /** Identifiant, gabarit possible pour un port répété : « u{i+1} ». */
  id: z.string().min(1).max(60),
  label: z.string().max(80).optional(),
  pos: Vec3,
  /** Direction sortante (facultative) : sert à l'affichage et aux poses orientées. */
  dir: Vec3.optional(),
  repeat: Repeat,
});

export const KitLineSchema = z.object({
  ref: z.string().min(1).max(60),
  name: z.string().min(1).max(120),
  qty: Expr,
  price: Num.nonnegative().optional(),
});

/** « percage » : trou seul (taquet, ligne de trous…) — aucune quincaillerie comptée. */
export const FIXING_TYPES = ['vis', 'clou', 'boulon', 'cheville', 'tourillon', 'rivet', 'excentrique', 'agrafe', 'percage'];

/** Emplacement de fixation : point + direction SORTANTE (côté tête), type, Ø et longueur.
 *  Compté en nomenclature (quincaillerie) et affiché en 3D. */
export const FixingSchema = z.object({
  type: z.enum(FIXING_TYPES).default('vis'),
  pos: Vec3,
  dir: Vec3.default([0, 0, 1]),
  d: Expr.default(4),
  l: Expr.default(20),
  ref: z.string().max(60).optional(),
  name: z.string().max(120).optional(),
  price: Num.nonnegative().optional(),
  repeat: Repeat,
});

/** Logement (encoche, feuillure, rainure) : pavé que le composant creuse dans les pièces
 *  à percer qu'il touche — p. ex. la palette d'un taquet encastrée sous la tablette pour
 *  qu'elle ne glisse pas. `pos` = coin minimal, `size` = [x, y, z], repère du composant. */
export const RecessSchema = z.object({
  name: z.string().max(80).optional(),
  size: Vec3,
  pos: Vec3.default([0, 0, 0]),
  repeat: Repeat,
});

/** Articulation : charnière (axe + point de l'axe, angle en degrés) ou glissière
 *  (direction, course en mm). L'ouverture courante est portée par l'instance. */
export const JointSchema = z.object({
  type: z.enum(['hinge', 'slide']),
  label: z.string().max(60).optional(),
  axis: Vec3,
  origin: Vec3.default([0, 0, 0]),
  min: Expr.default(0),
  max: Expr,
});

export const CATEGORIES = ['alu', 'meuble', 'rack', 'quincaillerie', 'autre'];

export const ComponentSchema = z.object({
  id: Id,
  name: z.string().min(1).max(120),
  category: z.enum(CATEGORIES).default('autre'),
  family: z.string().max(80).optional(),
  description: z.string().max(2000).optional(),
  ref: z.string().max(80).optional(),
  supplier: z.string().max(80).optional(),
  material: z.string().max(80).optional(),
  color: Color.default('#b8bec6'),
  params: z.array(ParamSchema).max(30).default([]),
  parts: z.array(PartSchema).min(1).max(500),
  ports: z.array(PortSchema).max(200).default([]),
  bom: z.object({
    /** piece = une ligne par variante ; cut = barre débitée (longueur = lengthExpr) ; none = hors nomenclature */
    kind: z.enum(['piece', 'cut', 'none']).default('piece'),
    /** Gabarit de désignation des dimensions : « {W}×{H}×{E} ». */
    label: z.string().max(120).optional(),
    lengthExpr: Expr.optional(),
    /** Prix unitaire (€/pièce, expression possible : « W*H/1e6*35 ») ou, pour un débit, prix au mètre. */
    price: Expr.optional(),
    pricePerM: Num.nonnegative().optional(),
    /** Section de référence d'un débit (pour regrouper les barres). */
    stock: z.string().max(60).optional(),
  }).prefault({}),
  kit: z.array(KitLineSchema).max(30).default([]),
  fixings: z.array(FixingSchema).max(200).default([]),
  recesses: z.array(RecessSchema).max(100).default([]),
  joint: JointSchema.optional(),
  /** Pièce à percer (plans de perçage de la notice). Par défaut : panneaux et bois
   *  (catégories meuble / autre hors quincaillerie) — pas les profilés à rainure ni l'acheté. */
  drill: z.boolean().optional(),
});

export const TemplateInstanceSchema = z.object({
  /** Clé locale au gabarit, pour qu'une autre instance s'y attache (parent). */
  key: z.string().max(40).optional(),
  /** Clé du parent : la pose de cette instance est alors RELATIVE au parent
   *  (même indice de répétition que lui si les deux sont répétées). */
  parent: z.string().max(40).optional(),
  componentId: Id,
  name: z.string().max(120).optional(),
  params: z.record(z.string(), Expr).default({}),
  pos: Vec3.default([0, 0, 0]),
  rot: Vec3.default([0, 0, 0]),
  repeat: Repeat,
  step: z.number().int().min(1).optional(),
});

export const TemplateSchema = z.object({
  id: Id,
  name: z.string().min(1).max(120),
  category: z.enum(CATEGORIES).default('autre'),
  description: z.string().max(2000).optional(),
  params: z.array(ParamSchema).max(30).default([]),
  instances: z.array(TemplateInstanceSchema).min(1).max(500),
});

export const InstanceSchema = z.object({
  id: Id,
  componentId: Id,
  name: z.string().max(120).optional(),
  params: z.record(z.string(), Num).default({}),
  pos: NumVec3.default([0, 0, 0]),
  rot: NumVec3.default([0, 0, 0]),
  groupId: Id.nullable().default(null),
  /** Instance porteuse : pos/rot sont alors exprimées dans son repère (articulation comprise). */
  parentId: Id.nullable().default(null),
  /** Ouverture de l'articulation du composant (degrés ou mm). */
  open: Num.default(0),
  hidden: z.boolean().default(false),
  color: Color.optional(),
  /** Étape de montage (notice) — null = non affectée. */
  step: z.number().int().min(1).nullable().default(null),
  note: z.string().max(500).optional(),
});

export const GroupSchema = z.object({
  id: Id,
  name: z.string().min(1).max(120),
  parentId: Id.nullable().default(null),
  hidden: z.boolean().default(false),
  collapsed: z.boolean().default(false),
});

export const ExtraSchema = z.object({
  id: Id,
  ref: z.string().max(60).default(''),
  name: z.string().min(1).max(120),
  qty: Num.nonnegative(),
  unit: z.string().max(10).default('pce'),
  price: Num.nonnegative().optional(),
});

export const SheetSchema = z.object({
  id: Id,
  material: z.string().max(80).default(''),
  thickness: Num.positive().max(200),
  length: Num.positive().max(10000),
  width: Num.positive().max(10000),
  price: Num.nonnegative().optional(),
  grain: z.boolean().default(false),
  /** Rognage propre à ce format (mm par bord) : 0 pour une planche aux chants finis.
   *  Absent = rognage du projet (sheetTrim). */
  trim: Num.nonnegative().max(100).optional(),
});

export const ProjectSchema = z.object({
  id: Id,
  name: z.string().min(1).max(100),
  version: z.literal(1).default(1),
  owner: z.string().nullable().optional(),
  /** Contributeurs (lecture + écriture) et lecteurs (lecture seule) — comptes du portail. */
  sharedWith: z.array(z.string()).optional(),
  viewers: z.array(z.string()).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  settings: z.object({
    grid: Num.positive().default(10),
    snapAngle: Num.positive().default(15),
    snapPorts: z.boolean().default(true),
    /** Longueur des barres du commerce pour l'optimisation de débit (mm) et trait de scie. */
    barLength: Num.positive().default(6000),
    kerf: Num.nonnegative().default(3),
    /** Notice de montage : angle de vue des illustrations, pièces éclatées. */
    noticeView: z.enum(['av-d', 'av-g', 'ar-d', 'ar-g']).default('av-d'),
    noticeExplode: z.boolean().default(false),
    /** Plan de coupe des panneaux : formats disponibles chez le fournisseur. Matière vide =
     *  tout panneau de cette épaisseur. grain = fil / décor : pas de rotation des pièces. */
    sheets: z.array(SheetSchema).max(50).default([]),
    /** Trait de la scie à panneaux et rognage des bords de chaque panneau (mm). */
    sheetKerf: Num.nonnegative().max(20).default(4),
    sheetTrim: Num.nonnegative().max(100).default(10),
  }).prefault({}),
  meta: z.object({
    client: z.string().max(120).optional(),
    reference: z.string().max(60).optional(),
    author: z.string().max(80).optional(),
    notes: z.string().max(4000).optional(),
  }).prefault({}),
  components: z.array(ComponentSchema).max(500).default([]),
  groups: z.array(GroupSchema).max(1000).default([]),
  instances: z.array(InstanceSchema).max(5000).default([]),
  extras: z.array(ExtraSchema).max(500).default([]),
});

export function createEmptyProject(name) {
  const now = new Date().toISOString();
  return ProjectSchema.parse({ id: newId('p'), name, createdAt: now, updatedAt: now });
}

function issues(err) {
  return err.issues.slice(0, 20).map((i) => `${i.path.join('.')} : ${i.message}`);
}

export function validateProject(raw) {
  const r = ProjectSchema.safeParse(raw);
  return r.success ? { ok: true, project: r.data } : { ok: false, errors: issues(r.error) };
}

export function validateComponent(raw) {
  const r = ComponentSchema.safeParse(raw);
  return r.success ? { ok: true, component: r.data } : { ok: false, errors: issues(r.error) };
}

export function validateTemplate(raw) {
  const r = TemplateSchema.safeParse(raw);
  return r.success ? { ok: true, template: r.data } : { ok: false, errors: issues(r.error) };
}
